import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash.debounce';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { SimpleModal } from '../../../../components/simple-modal';
import { OrderSearchService } from '../../../../services/order-search.service';
import Global from '../../../../store/global-selectors';
import * as Actions from '../../../../store/scan-to-print/scan-to-print.actions';
import { usePlanFeatureGate } from '../../../../feature-gates/use-plan-feature-gate';
import { Features } from '../../../../constants/features';

import { messages } from '../../../messages';
import style from './scan-search.module.less';
import { useEnableAddToPickingQueue } from '../../../../feature-flags/hooks/use-enable-add-to-picking-queue';
import { noop } from 'ui-components/default-props/noop';
import { useModal } from '../../../../hooks/use-modal';

export const ScanSearch = ({ inactive, trackScanStart = noop }) => {
  const dispatch = useDispatch();
  const searchValue = useSelector(Global.selectScanToPrintSearchValue);
  const shipmentNumber = useSelector(Global.selectScanToPrintFulfillmentPlanId);
  const { isActive: isScanQueueActive = false } = useSelector(Global.selectScanToPrintQueue);
  const { open, close, isOpen } = useModal();
  const [pendingSearchValue, setPendingSearchValue] = useState('');
  const inputRef = useRef(null);
  const onSearchChange = useCallback((value) => dispatch(Actions.setScanSearchValue(value)), [dispatch]);
  const onSearch = useCallback(
    (value) => {
      if (isScanQueueActive) {
        setPendingSearchValue(value);
        open();
        return;
      }
      trackScanStart();
      dispatch(Actions.searchForOrderNumber(value));
    },
    [isScanQueueActive, trackScanStart, dispatch, open],
  );

  const handleConfirmQueueInterruption = useCallback(() => {
    dispatch(Actions.clearScanQueue());
    trackScanStart();
    dispatch(Actions.searchForOrderNumber(pendingSearchValue));
    close();
    setPendingSearchValue('');
  }, [dispatch, trackScanStart, pendingSearchValue, close]);

  const handleCancelQueueInterruption = useCallback(() => {
    close();
    setPendingSearchValue('');
  }, [close]);
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(onSearch, 500), [onSearch]);

  useEffect(() => {
    if (!inactive && !isScanQueueActive) {
      inputRef.current.focus();
    }
  }, [inactive, isScanQueueActive, shipmentNumber]);

  const handleChange = (event) => {
    const val = event.target.value;
    onSearchChange(val);
    // this is used when a user has the input focused
    // and scans a barcode that has orderId
    if (OrderSearchService.isThisAnOrderId(val)) {
      debouncedSearch(val);
    }
  };

  const submitForm = (event) => {
    if (event) {
      event.preventDefault();
    }

    if (searchValue) {
      onSearch(searchValue);
      inputRef.current.blur();
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      submitForm(event);
    }
  };

  const buttonTheme = inactive ? 'dark' : 'primary';

  const isTotesEnabled = useEnableAddToPickingQueue();
  const { isFeatureEnabled: hasAccessToTotes } = usePlanFeatureGate(Features.WarehouseMobileTotePicking);
  const searchLabel = isTotesEnabled && hasAccessToTotes ? messages.scanSearchLabelTotes : messages.scanSearchLabelBasic;

  return (
    <>
      <FlexColumn className={style.wrapper}>
        <Text textType={TextType.Label} value={searchLabel} />
        <form className={style['button-wrapper']} onSubmit={submitForm}>
          <input
            ref={inputRef}
            autoComplete='off'
            className={classnames(style.input, { [style.dark]: inactive })}
            id='scan-search-box'
            onChange={handleChange}
            onKeyDown={onKeyDown}
            type='text'
            value={searchValue}
          />
          <Button
            className={classnames(style['submit-button'], {
              [style.dark]: inactive,
            })}
            onClick={submitForm}
            theme={buttonTheme}
          >
            <FormattedMessage {...messages.searchShipmentButton} />
          </Button>
        </form>
      </FlexColumn>
      {isOpen && (
        <SimpleModal
          actionButtonTheme='danger'
          actionMessage={messages.queueInterruptionConfirmStop}
          cancelMessage={messages.queueInterruptionConfirmCancel}
          headerMessage={messages.queueInterruptionConfirmTitle}
          onAction={handleConfirmQueueInterruption}
          onCancel={handleCancelQueueInterruption}
          onClose={handleCancelQueueInterruption}
          size='medium'
        >
          <Text textType={TextType.Paragraph} value={messages.queueInterruptionConfirmMessage} />
        </SimpleModal>
      )}
    </>
  );
};

ScanSearch.propTypes = {
  inactive: PropTypes.bool.isRequired,
};
